import {
  DEFAULT_LOCALE,
  RUNTIME_LOCALES,
  type LocaleCode,
  type RuntimeLocale,
  type TranslationDictionary,
} from './i18n.types';

const LOCALE_ALIASES: Record<string, LocaleCode> = {
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  en: 'en-US',
  'en-us': 'en-US',
  es: 'es-ES',
  'es-es': 'es-ES',
};

export function normalizeLocale(value: string | null | undefined): LocaleCode | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  return LOCALE_ALIASES[normalized] ?? null;
}

export function toRuntimeLocale(value: LocaleCode | null | undefined): RuntimeLocale | null {
  if (!value) return null;
  return (RUNTIME_LOCALES as readonly string[]).includes(value) ? (value as RuntimeLocale) : null;
}

/** Aceita `pt-PT`, `en_GB`, etc.: usa o subtag primário quando o par completo não está mapeado. */
function resolveLocaleCandidate(raw: string | null | undefined): LocaleCode | null {
  if (!raw?.trim()) return null;
  const direct = normalizeLocale(raw);
  if (direct) return direct;
  const primary = raw.trim().split(/[-_]/)[0]?.toLowerCase();
  if (!primary) return null;
  return normalizeLocale(primary);
}

export function detectBrowserLocale(): LocaleCode | null {
  if (typeof navigator === 'undefined') return null;

  const candidates = [
    ...(Array.isArray(navigator.languages) ? navigator.languages : []),
    navigator.language,
  ];

  for (const candidate of candidates) {
    const normalized = resolveLocaleCandidate(candidate);
    if (normalized) return normalized;
  }

  return null;
}

export function resolveByPriority(params: {
  companyLocale: LocaleCode | null;
  localLocale: LocaleCode | null;
  browserLocale: LocaleCode | null;
  /** Login / onboarding / sem workspace: idioma do navegador antes do que está guardado localmente. */
  preferBrowserOverLocal?: boolean;
}): { locale: RuntimeLocale; source: 'company' | 'local' | 'browser' | 'default' } {
  const companyRuntime = toRuntimeLocale(params.companyLocale);
  if (companyRuntime) return { locale: companyRuntime, source: 'company' };

  if (params.preferBrowserOverLocal) {
    const browserRuntime = toRuntimeLocale(params.browserLocale);
    if (browserRuntime) return { locale: browserRuntime, source: 'browser' };

    const localRuntime = toRuntimeLocale(params.localLocale);
    if (localRuntime) return { locale: localRuntime, source: 'local' };
  } else {
    const localRuntime = toRuntimeLocale(params.localLocale);
    if (localRuntime) return { locale: localRuntime, source: 'local' };

    const browserRuntime = toRuntimeLocale(params.browserLocale);
    if (browserRuntime) return { locale: browserRuntime, source: 'browser' };
  }

  return { locale: DEFAULT_LOCALE, source: 'default' };
}

export function getByPath(
  dictionary: TranslationDictionary | undefined,
  path: string
): string | null {
  if (!dictionary || !path) return null;

  const segments = path
    .split('.')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean);
  let current: unknown = dictionary;

  for (const segment of segments) {
    if (typeof current !== 'object' || current == null) return null;
    current = (current as Record<string, unknown>)[segment];
  }

  return typeof current === 'string' ? current : null;
}

export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
    const value = params[key];
    return value == null ? '' : String(value);
  });
}

export function normalizeDictionaryKeysToLowercase(
  dictionary: TranslationDictionary
): TranslationDictionary {
  const normalizeValue = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map((item) => normalizeValue(item));
    }
    if (value && typeof value === 'object') {
      const source = value as Record<string, unknown>;
      const target: Record<string, unknown> = {};
      for (const [key, child] of Object.entries(source)) {
        target[key.toLowerCase()] = normalizeValue(child);
      }
      return target;
    }
    return value;
  };

  return normalizeValue(dictionary) as TranslationDictionary;
}
